import { FC, useState, useEffect, useCallback, useMemo } from 'react';
import { KatFileUpload, KatButton } from '@amzn/katal-react';
import CropIcon from '@mui/icons-material/Crop';
import { MaterialRadioButtonGroup } from 'src/components/general/MaterialRadioButtonGroup';
import { ConfirmationDialog } from 'src/components/general/ConfirmationDialog';
import { CropModal, CropOrientation } from 'src/components/general/CropModal/CropModal';
import { StatusIndicator } from 'src/components/general/StatusIndicator';
import { useAssetInput } from 'src/hooks/assetInput/useAssetInput';
import { InputType, AssetStatus } from 'src/types';
import { UrlInput } from './UrlInput';
import { SearchImageInput } from 'src/components/pages/campaignAutomation/input/assets/SearchImageInput';
import { AssetInputProps, INPUT_OPTIONS } from '.';
import styles from './AssetInput.module.css';
import { getFileUploadState } from 'src/utils/assetInput/assetInputUtils';
import { AssetConstants, StateConstants } from 'src/constants';
import { StyledDropdown } from 'src/components/general/StyledDropdown';
import {
  getVariantsFromConfig,
  VIDEO_TRAILER_CREATIVE_VARIANTS,
  VIDEO_TRAILER_UPLOAD_VARIANTS,
} from 'src/config/templateVariantConfiguration';
import { ProvisionedConstants, STAGE } from 'src/constants';
import { useAssetContext } from 'src/hooks/useAssetContext';
import { VideoDurationInput } from './VideoDurationInput';
import {
  AssetType,
  DeviceFamily,
  Placement,
  TransformationVariant,
  SourceAssetMediaType,
} from '@amzn/fire-tv-campaign-automation-service';
import { LogoTextInput } from './LogoTextInput';
import { useAssetOperationsInProgress } from 'src/hooks/useAssetOperationsInProgress';
import { useSelection } from 'src/contexts/SelectionContext';
import { OverlaySection } from './OverlaySection';
import { FeatureFlag, isFeatureEnabled } from 'src/featureFlags';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag';
import { MultiTitleHorizontalInput } from './MultiTitleHorizontalInput';
import { MultiTitleVerticalInput, MultiTitleVerticalOptions } from './MultiTitleVerticalInput';
import { ImageEntry } from 'src/components/general/MultiImageInput';
import { TriviaTrailerInput } from './TriviaTrailerInput';
import { GamifiedTrailerInput } from './GamifiedTrailerInput';
import { AssetInputHandler } from 'src/handlers/AssetInputHandler';

const CUSTOM_INPUT_VARIANTS: TransformationVariant[] = [
  TransformationVariant.MULTI_TITLE_HORIZONTAL,
  TransformationVariant.MULTI_TITLE_VERTICAL,
  TransformationVariant.TRIVIA_TRAILER,
  TransformationVariant.GAMIFIED_TRAILER,
];

const ENABLED_STATUSES_FOR_VIDEO_DURATION = [
  AssetStatus.COMPLETED,
  AssetStatus.PENDING_TRANSFORMATION,
  AssetStatus.BASIC_VALIDATION_SUCCESS,
];

const ENABLED_STATUSES_FOR_CROP_MODAL = [
  AssetStatus.BASIC_VALIDATION_SUCCESS,
  AssetStatus.COMPLETED,
];

const SEARCH_ENABLED_STATUSES: readonly AssetStatus[] = [
  AssetStatus.FAILED,
  AssetStatus.COMPLETED,
  AssetStatus.PENDING_INPUT,
  AssetStatus.BASIC_VALIDATION_FAILED,
];

/**
 * Component for handling asset input through various methods (URL, file upload, or logo text)
 * @param {AssetInputProps} props - The component props
 * @returns {JSX.Element} Asset input component with radio buttons for input type selection
 */
export const AssetInput: FC<AssetInputProps> = ({
  label,
  assetInputOptions,
  assetInputHandler,
  acceptedFileTypes,
  maxFileSizeInBytes,
  assetTransformationHandler,
  error,
  status,
  assetType,
  assetDetails,
  assetValidationHandler,
  setState,
  disabled = false,
}) => {
  const {
    selectedInputType,
    url,
    showConfirmDialog,
    radioGroupName,
    isValidUrlInput,
    selectedImageTitle,
    deviceFamily,
    placement,
    handleUrlChange,
    handleUrlFetch,
    handleCatalogSelection,
    handleUrlRemoval,
    handleFileAttached,
    handleFileRemoved,
    handleFileReplaced,
    handleFileAttachedError,
    handleRadioChange,
    handleConfirmTypeChange,
    handleCancelTypeChange,
    handleTransformationVariantChange,
    handleClearSelectedImage,
    getConfirmationMessage,
  } = useAssetInput({
    label,
    assetType,
    assetInputHandler,
    status,
    setState,
    assetDetails,
    assetValidationHandler,
    assetTransformationHandler,
  });

  const { assetStates } = useAssetContext();
  const { marketplace } = useSelection();
  const isOverlaySectionEnabled = useFeatureFlag(FeatureFlag.OverlaySection);
  const fileUploadStatus = getFileUploadState(status, error);
  const radioButtonInputOptions = Object.keys(assetInputOptions)
    .map((option) => INPUT_OPTIONS[option as InputType])
    .filter((option): option is NonNullable<typeof option> => option !== undefined);

  const isAssetOperationInProgress = useAssetOperationsInProgress(assetType);

  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [imageKey, setImageKey] = useState<string | undefined>();

  const transformationVariant = assetStates[assetType]?.transformationVariant;

  const getCropOrientation = (): CropOrientation | undefined => {
    if (assetType !== AssetType.BACKGROUND_IMAGE) return undefined;
    if (transformationVariant === TransformationVariant.FULL_BLEED) return 'vertical';
    if (
      transformationVariant === TransformationVariant.DOMINANT_COLOR_FADED_OVERLAY &&
      placement === Placement.SCREEN_SAVER
    )
      return 'horizontal';
    return undefined;
  };

  const cropOrientation = getCropOrientation();

  const showCropControls = cropOrientation !== undefined;

  // Manage blob URL lifecycle
  useEffect(() => {
    if (assetDetails.file) {
      const url = URL.createObjectURL(assetDetails.file);
      setImageKey(url);
      return () => URL.revokeObjectURL(url);
    }
    setImageKey(undefined);
  }, [assetDetails.file]);

  useEffect(() => {
    if (showCropControls && status === AssetStatus.BASIC_VALIDATION_SUCCESS) {
      setIsCropModalOpen(true);
    }
  }, [showCropControls, status]);

  const imagesToFiles = useCallback(async (images: ImageEntry[]): Promise<File[]> => {
    const files: File[] = [];
    for (const img of images) {
      if (img.file) {
        files.push(img.file);
      } else if (img.url) {
        const resp = await fetch(img.url);
        if (!resp.ok) throw new Error(`Failed to fetch image: ${resp.status}`);
        const blob = await resp.blob();
        const mimeType = blob.type || 'image/jpeg';
        const ext = mimeType.split('/')[1]?.replace('jpeg', 'jpg') || 'jpg';
        const baseName = img.name.replace(/\.[^.]+$/, '');
        const fileName = `${baseName}.${ext}`;
        files.push(new File([blob], fileName, { type: mimeType }));
      }
    }
    return files;
  }, []);

  const handleMultiTitleHorizontalGenerate = useCallback(
    async (images: ImageEntry[]) => {
      try {
        const files = await imagesToFiles(images);
        await assetInputHandler.handleMultipleFiles(files, SourceAssetMediaType.IMAGE);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to process images';
        setState({ status: AssetStatus.FAILED, error: message });
      }
    },
    [assetInputHandler, setState, imagesToFiles],
  );

  const handleMultiTitleVerticalGenerate = useCallback(
    async (images: ImageEntry[], options: MultiTitleVerticalOptions) => {
      try {
        const files = await imagesToFiles(images);
        setState({
          dividerStyle: options.dividerStyle,
          leftOffsets: options.leftOffsets,
          topOffsets: options.topOffsets,
          forceScreensaver: options.forceScreensaver,
          enableSmartProcessing: options.enableSmartProcessing,
        });
        await assetInputHandler.handleMultipleFiles(files, SourceAssetMediaType.IMAGE);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to process images';
        setState({ status: AssetStatus.FAILED, error: message });
      }
    },
    [assetInputHandler, setState, imagesToFiles],
  );

  // Determine if this variant has a custom input replacing standard radio/input controls
  const customVariantInput = (() => {
    if (!CUSTOM_INPUT_VARIANTS.includes(transformationVariant as TransformationVariant))
      return null;
    switch (transformationVariant) {
      case TransformationVariant.MULTI_TITLE_HORIZONTAL:
        return (
          <MultiTitleHorizontalInput
            disabled={disabled}
            assetState={assetStates[assetType]}
            marketplace={marketplace}
            setState={setState}
            onGenerate={handleMultiTitleHorizontalGenerate}
            onCancel={() => setState({ status: AssetStatus.PENDING_INPUT })}
          />
        );
      case TransformationVariant.MULTI_TITLE_VERTICAL:
        return (
          <MultiTitleVerticalInput
            disabled={disabled}
            assetState={assetStates[assetType]}
            marketplace={marketplace}
            setState={setState}
            onGenerate={handleMultiTitleVerticalGenerate}
            onCancel={() => setState({ status: AssetStatus.PENDING_INPUT })}
          />
        );
      case TransformationVariant.TRIVIA_TRAILER:
        return (
          <TriviaTrailerInput
            disabled={disabled}
            assetInputHandler={assetInputHandler as AssetInputHandler}
            setState={setState}
          />
        );
      case TransformationVariant.GAMIFIED_TRAILER:
        return <GamifiedTrailerInput updateAssetState={setState} disabled={disabled} />;
      default:
        return null;
    }
  })();

  const hasStandardInput = customVariantInput === null;

  const renderInput = () => {
    switch (selectedInputType) {
      case InputType.URL:
        return (
          <UrlInput
            url={url}
            status={status}
            onChange={handleUrlChange}
            onFetch={handleUrlFetch}
            onClear={handleUrlRemoval}
            isValidUrl={isValidUrlInput}
            isFetching={status === AssetStatus.PENDING_UPLOAD}
            disabled={disabled}
          />
        );
      case InputType.UPLOAD:
        const isUploadDisabled = disabled || isAssetOperationInProgress;
        return (
          <div
            style={{
              pointerEvents: isUploadDisabled ? 'none' : 'auto',
              opacity: isUploadDisabled ? 0.5 : 1,
            }}
          >
            <KatFileUpload
              accept={acceptedFileTypes}
              maxSize={maxFileSizeInBytes}
              onFilesAttached={(event) => handleFileAttached(event)}
              onFilesRemoved={handleFileRemoved}
              onFilesReplaced={(event) => handleFileReplaced(event)}
              onFilesAttachedError={handleFileAttachedError}
              hideClearAll
              state={fileUploadStatus}
              disabled={isUploadDisabled}
              data-testid="file-upload"
            >
              <div slot="error">{error}</div>
            </KatFileUpload>
          </div>
        );
      case InputType.SEARCH:
        return (
          <SearchImageInput
            label="Search by Title or Keyword"
            placeholder="Enter movie or content name"
            onImageSelect={(imageUrl: string, imageTitle?: string) =>
              handleCatalogSelection(imageUrl, imageTitle)
            }
            selectedImageUrl={url}
            selectedImageTitle={selectedImageTitle}
            onClearSelection={handleClearSelectedImage}
            maxLength={AssetConstants.SEARCH_QUERY_MAX_LENGTH}
            disabled={disabled || !SEARCH_ENABLED_STATUSES.includes(status)}
          />
        );
      case InputType.LOGO_TEXT:
        return (
          <LogoTextInput
            logoTextState={assetStates[assetType]}
            updateAssetState={setState}
            disabled={disabled}
          />
        );

      default:
        return null;
    }
  };

  // For VIDEO_TRAILER, filter dropdown variants based on selected input type
  const filteredVariantOptions = useMemo(() => {
    const allVariants = getVariantsFromConfig(
      ProvisionedConstants.STAGE as STAGE,
      deviceFamily,
      placement,
    )?.[assetType];
    if (!allVariants || assetType !== AssetType.VIDEO_TRAILER) return allVariants;

    const filterSet =
      selectedInputType === InputType.CREATE_YOUR_OWN
        ? VIDEO_TRAILER_CREATIVE_VARIANTS
        : VIDEO_TRAILER_UPLOAD_VARIANTS;
    return allVariants.filter((v) => filterSet.has(v.value as TransformationVariant));
  }, [assetType, selectedInputType, deviceFamily, placement]);

  return (
    <div className={styles.container} data-testid="asset-input">
      {label && <label className={styles.assetTitle}>{label}</label>}

      {(hasStandardInput || assetType === AssetType.VIDEO_TRAILER) && (
        <MaterialRadioButtonGroup
          name={radioGroupName}
          options={radioButtonInputOptions}
          value={selectedInputType}
          onChange={handleRadioChange}
          data-testid="input-type-selector"
          disabled={disabled}
        />
      )}

      {assetInputOptions[selectedInputType as InputType] && (
        <StyledDropdown
          options={filteredVariantOptions}
          value={assetStates[assetType]?.transformationVariant}
          onChange={handleTransformationVariantChange}
          label={'Transformation Template'}
          data-testid={`${assetType}-transformation-variant-selector`}
          disabled={disabled}
        />
      )}

      {hasStandardInput && renderInput()}

      {showCropControls && assetDetails.file && (
        <div className={styles.editCropContainer}>
          <KatButton
            variant="secondary"
            size="small"
            onClick={() => setIsCropModalOpen(true)}
            disabled={!ENABLED_STATUSES_FOR_CROP_MODAL.includes(status)}
            data-testid="edit-crop-button"
          >
            <CropIcon fontSize="small" className={styles.cropIcon} />{' '}
            <span className={styles.cropText}>Crop Image</span>
          </KatButton>
        </div>
      )}

      {assetType === AssetType.VIDEO_TRAILER &&
        hasStandardInput &&
        assetStates[assetType]?.transformationVariant !==
          TransformationVariant.NO_TRANSFORMATION && (
          <VideoDurationInput
            videoState={assetStates[assetType]}
            updateAssetState={setState}
            videoDuration={assetStates[assetType]?.videoDuration}
            disabled={!ENABLED_STATUSES_FOR_VIDEO_DURATION.includes(status)}
            data-testid="video-duration-input"
          />
        )}

      {customVariantInput}

      {showCropControls && ENABLED_STATUSES_FOR_CROP_MODAL.includes(status) && (
        <CropModal
          isVisible={isCropModalOpen}
          onClose={() => setIsCropModalOpen(false)}
          imageState={assetStates[assetType]}
          updateAssetState={setState}
          imageKey={imageKey}
          disabled={!ENABLED_STATUSES_FOR_CROP_MODAL.includes(status)}
          orientation={cropOrientation}
          data-testid="crop-modal"
        />
      )}

      {hasStandardInput &&
        assetType === AssetType.BACKGROUND_IMAGE &&
        deviceFamily === DeviceFamily.FIRE_TV &&
        isOverlaySectionEnabled && (
          <OverlaySection
            assetState={assetStates[assetType]}
            marketplace={marketplace}
            enabled={status === AssetStatus.COMPLETED && !!assetStates[assetType]?.key}
            setState={setState}
          />
        )}

      <StatusIndicator
        // for Input type of file upload input error is thrown in KatFileUpload component
        error={selectedInputType !== InputType.UPLOAD ? error : StateConstants.EMPTY_STRING}
        isLoading={isAssetOperationInProgress}
        isSuccess={status === AssetStatus.COMPLETED}
        data-testid="asset-input-status"
      />

      <ConfirmationDialog
        isVisible={showConfirmDialog}
        title="Change Input"
        message={getConfirmationMessage()}
        onConfirm={handleConfirmTypeChange}
        onCancel={handleCancelTypeChange}
        data-testid="asset-input-confirm-dialog"
      />
    </div>
  );
};
